'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { retrieve, store, STORAGE_KEYS } from '@/utils/storage';

const SCROLL_RESTORE_RETRY_DURATION_MS = 1200;

const scrollRestorePaths = new Set<string>();

type ScrollPositionsByPath = Record<string, number>;

const savedPositions = {
    readAll: (): ScrollPositionsByPath => {
        const raw = retrieve(STORAGE_KEYS.SCROLL_POSITION);
        if (!raw) return {};
        try {
            const parsed: unknown = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? (parsed as ScrollPositionsByPath) : {};
        } catch {
            return {};
        }
    },
    writeAll: (positions: ScrollPositionsByPath) => {
        store({ key: STORAGE_KEYS.SCROLL_POSITION, value: JSON.stringify(positions) });
    },
    get: (path: string): number => Number(savedPositions.readAll()[path]),
    set: (path: string, scrollY: number) => {
        savedPositions.writeAll({ ...savedPositions.readAll(), [path]: scrollY });
    },
    clear: (path: string) => {
        const positions = savedPositions.readAll();
        delete positions[path];
        savedPositions.writeAll(positions);
    }
};

export const saveScrollPosition = (path: string, scrollY: number) => {
    if (scrollRestorePaths.has(path)) {
        savedPositions.set(path, scrollY);
    }
};

export const restoreScrollPosition = (path: string) => {
    const targetScrollY = savedPositions.get(path);
    if (!targetScrollY) return;

    const retryDeadline = performance.now() + SCROLL_RESTORE_RETRY_DURATION_MS;
    const tryScrollTo = () => {
        if (window.location.pathname !== path) return;

        const maxScrollY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
        const canReachTarget = maxScrollY >= targetScrollY;
        const timedOut = performance.now() > retryDeadline;

        if (canReachTarget || timedOut) {
            window.scrollTo(0, Math.min(targetScrollY, maxScrollY));
            savedPositions.clear(path);
        } else {
            requestAnimationFrame(tryScrollTo);
        }
    };
    requestAnimationFrame(tryScrollTo);
};

export const RestoreScrollOnReturn = () => {
    const pathname = usePathname();
    useEffect(() => {
        scrollRestorePaths.add(pathname);

        // Full-document back load: native scroll restoration and our restore race — native can fire last and
        // reset to 0 (snap-back). 'manual' disables native so ours wins; history is shared, so revert on unmount.
        const supportsScrollRestoration = 'scrollRestoration' in window.history;
        const previousScrollRestoration = window.history.scrollRestoration;
        if (supportsScrollRestoration) {
            window.history.scrollRestoration = 'manual';
        }

        restoreScrollPosition(pathname);

        return () => {
            scrollRestorePaths.delete(pathname);
            if (supportsScrollRestoration) {
                window.history.scrollRestoration = previousScrollRestoration;
            }
        };
    }, [pathname]);
    return null;
};
